import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Send, Clock, Loader2 } from "lucide-react";
import PageHero from "../components/PageHero";
import { COMPANY, SERVICES } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Champs requis", description: "Veuillez remplir votre nom, courriel et message." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: "Message envoyé !", description: "Merci, nous vous répondrons dans les plus brefs délais." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Erreur", description: "L'envoi a échoué. Veuillez réessayer ou nous écrire directement." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded border border-slate-200 text-slate-700 text-sm focus:outline-none focus:border-[#2f80c4] focus:ring-1 focus:ring-[#2f80c4] transition-colors";

  return (
    <div>
      <PageHero
        title="Nous joindre"
        subtitle="Discutons de vos enjeux en gouvernance TI et en agilité."
        image="https://images.pexels.com/photos/32168739/pexels-photo-32168739.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600"
      />

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Info */}
          <div className="lg:col-span-1">
            <div className="w-14 h-[3px] bg-[#2f80c4] mb-6" />
            <h2 className="text-2xl font-bold text-[#15324f] mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>Coordonnées</h2>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#2f80c4]/10 flex items-center justify-center shrink-0"><MapPin size={20} className="text-[#2f80c4]" /></span>
                <div>
                  <p className="font-semibold text-[#15324f]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Adresse</p>
                  <p className="text-slate-600 text-sm mt-1">{COMPANY.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#2f80c4]/10 flex items-center justify-center shrink-0"><Phone size={20} className="text-[#2f80c4]" /></span>
                <div>
                  <p className="font-semibold text-[#15324f]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Téléphone</p>
                  <a href={`tel:${COMPANY.phoneHref}`} className="text-slate-600 text-sm mt-1 hover:text-[#2f80c4]">{COMPANY.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#2f80c4]/10 flex items-center justify-center shrink-0"><Mail size={20} className="text-[#2f80c4]" /></span>
                <div>
                  <p className="font-semibold text-[#15324f]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Courriel</p>
                  <a href={`mailto:${COMPANY.email}`} className="text-slate-600 text-sm mt-1 hover:text-[#2f80c4]">{COMPANY.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#2f80c4]/10 flex items-center justify-center shrink-0"><Clock size={20} className="text-[#2f80c4]" /></span>
                <div>
                  <p className="font-semibold text-[#15324f]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Heures d'ouverture</p>
                  <p className="text-slate-600 text-sm mt-1">Lundi au vendredi : 8 h 30 – 17 h</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-[#f4f6f9] rounded-lg p-8 md:p-10">
              <h2 className="text-2xl font-bold text-[#15324f] mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Demander une consultation
              </h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#15324f] mb-2">Nom complet *</label>
                  <input className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Votre nom" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#15324f] mb-2">Courriel *</label>
                  <input type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@exemple.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#15324f] mb-2">Téléphone</label>
                  <input className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 …" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#15324f] mb-2">Sujet</label>
                  <select className={inputCls} value={form.subject} onChange={(e) => update("subject", e.target.value)}>
                    <option value="">Choisir un sujet…</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.short}>{s.short}</option>
                    ))}
                    <option value="Formation">Formation</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#15324f] mb-2">Message *</label>
                  <textarea rows={5} className={inputCls} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Décrivez votre besoin…" />
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#15324f] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wide rounded hover:bg-[#2f80c4] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {submitting ? (<>Envoi en cours <Loader2 size={16} className="animate-spin" /></>) : (<>Envoyer <Send size={16} /></>)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="w-full h-[420px]">
        <iframe
          title="Carte Agile Connexion"
          src={COMPANY.mapEmbed}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
};

export default Contact;
